import React from 'react';
import { Edit3, Trash2, Package } from 'lucide-react';
import { Product } from '../../../../types';

interface ProductsListProps {
  products: Product[];
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
}

const ProductsList: React.FC<ProductsListProps> = ({
  products,
  onEdit,
  onDelete
}) => {
  if (products.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-xl border border-tinta/10 shadow-sm">
        <p className="text-tinta-soft italic">Nenhum produto encontrado</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
      {products.map(product => {
        const stockTone =
          product.stock > 10 ? 'text-musgo'
            : product.stock > 0 ? 'text-mostarda-dark'
              : 'text-bordo';
        const statusTone = product.is_active ? 'text-musgo' : 'text-tinta-soft';

        return (
          <div
            key={product.id}
            className="bg-white rounded-xl border border-tinta/10 hover:shadow-md hover:border-tinta/20 transition-all p-4"
          >
            {/* Product Image & Info */}
            <div className="mb-4 pb-3 border-b border-tinta/10">
              <div className="flex items-start gap-3 mb-3">
                {product.image_url ? (
                  <img
                    src={product.image_url}
                    alt={product.name}
                    className="w-20 h-20 rounded-lg object-cover flex-shrink-0 border border-tinta/10"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-20 h-20 bg-creme-light border border-tinta/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Package size={28} className="text-tinta-soft/60" />
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-tinta mb-1 leading-tight">{product.name}</h3>

                  <p className="text-sm text-tinta-soft line-clamp-1 text-ellipsis">{product.description}</p>
                  {product.is_no_prize && (
                    <div className="mt-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold uppercase tracking-wider bg-mostarda/20 text-mostarda-dark border border-mostarda/40">
                        Não foi dessa vez
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Stats */}
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Estoque</p>
                  <p className={`font-display text-lg font-bold ${stockTone}`}>
                    {product.stock}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-tinta-soft/70 font-semibold">Status</p>
                  <p className={`text-sm font-bold italic ${statusTone}`}>
                    {product.is_active ? 'Ativo' : 'Inativo'}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => onEdit(product)}
                className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-bordo bg-bordo/8 border border-bordo/20 rounded-lg hover:bg-bordo/15 transition-colors"
              >
                <Edit3 size={16} />
                Editar
              </button>
              <button
                onClick={() => onDelete(product)}
                className="flex items-center justify-center gap-2 py-2.5 px-3 text-sm font-bold text-tinta-soft bg-white border border-tinta/15 rounded-lg hover:bg-bordo/8 hover:text-bordo hover:border-bordo/30 transition-colors"
              >
                <Trash2 size={16} />
                Excluir
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProductsList;
